import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

interface MessageAttachmentProps {
	bucket: string;
	path: string;
	className?: string;
}

/**
 * Renders a chat image attachment via a short-lived signed URL.
 * Used for private buckets (e.g. chat-attachments).
 */
export const MessageAttachment = ({ bucket, path, className }: MessageAttachmentProps) => {
	const [url, setUrl] = useState<string | null>(null);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			const { data } = await supabase.storage.from(bucket).createSignedUrl(path, 60 * 60);
			if (!cancelled) setUrl(data?.signedUrl ?? null);
		})();
		return () => {
			cancelled = true;
		};
	}, [bucket, path]);

	if (!url) {
		return (
			<div
				className={cn(
					'h-32 w-32 rounded-lg bg-white/[0.05] animate-pulse',
					className
				)}
			/>
		);
	}

	return (
		<a
			href={url}
			target="_blank"
			rel="noopener noreferrer"
			className={cn('block rounded-lg overflow-hidden bg-white/[0.05] max-w-[220px]', className)}
		>
			<img src={url} alt="" className="w-full h-auto max-h-[260px] object-cover" />
		</a>
	);
};

export default MessageAttachment;
